import axios from "axios";
import { loadPreferredView, savePreferredView } from "@/Utile/ncrmViewPreferences.mjs";
import { rowActivationProps } from "@/Utile/tableRowKeyboard.mjs";
import {
  CONTRACTS_HOME_VIEW_PREFERENCE_KEY,
  formatDisplayDateParts,
  formatDisplayDateTimeParts,
  normalizeContractTableSortKey,
  normalizeContractTableSortOrder,
  parseDateLikeParts,
  parseDateTimeLikeParts,
  parseNumeric,
} from "./contractsHomeShared.mjs";
import { buildContractsListRequestParams, normalizeBoardStatusGroup } from "./contractsHomeFilters.mjs";
import { formatDisplayAmount, formatDisplayNumber } from "@/Utile/displayFormat.mjs";

export const contractsHomeMethods = {
  showSnackbar(message, color = "info") { this.snackbar = { show: true, color, message }; },
  formatNumber(value) {
    return formatDisplayNumber(parseNumeric(value, 0));
  },
  formatDate(value) {
    const parts = parseDateLikeParts(value);
    if (!parts) return String(value || "").trim() || "-";
    return formatDisplayDateParts(parts);
  },
  formatDateTime(value) {
    const parts = parseDateTimeLikeParts(value);
    if (!parts) return String(value || "").trim() || "-";
    return formatDisplayDateTimeParts(parts);
  },
  formatMoney(value, currency = "MDL") {
    const amount = parseNumeric(value, null);
    if (amount === null) return "-";
    return `${formatDisplayAmount(amount)} ${String(currency || "MDL").toUpperCase()}`;
  },
  statusLabel(status) {
    const normalized = String(status || "").trim().toLowerCase();
    if (!normalized) return "-";
    const key = `ncrm.contracts.statuses.${normalized}`;
    const translated = this.$t(key);
    return translated === key ? normalized : translated;
  },
  statusColor(status) {
    const normalized = String(status || "").trim().toLowerCase();
    if (normalized === "active" || normalized === "fulfilled") return "success";
    if (normalized === "draft") return "info";
    if (normalized === "expired") return "warning";
    if (normalized === "terminated" || normalized === "canceled") return "error";
    return "primary";
  },
  hasOverduePayments(item = {}) {
    return parseNumeric(item?.overdue_payment_count, 0) > 0;
  },
  isPaymentCardOverdue(item = {}) {
    if (parseNumeric(item?.past_due_payment_count, 0) > 0) return true;
    const dueParts = parseDateTimeLikeParts(item?.next_payment_due_at);
    if (!dueParts) return this.hasOverduePayments(item);
    const dueDate = new Date(dueParts.year, dueParts.month - 1, dueParts.day, dueParts.hour, dueParts.minute, dueParts.second || 0);
    return this.hasOverduePayments(item) || dueDate.getTime() < Date.now();
  },
  paymentCardAmount(item = {}) {
    return this.isPaymentCardOverdue(item)
      ? parseNumeric(item?.overdue_payment_amount, 0)
      : parseNumeric(item?.next_payment_amount, 0);
  },
  paymentCardAmountLabel(item = {}) {
    return this.formatMoney(this.paymentCardAmount(item), item?.currency_code);
  },
  paymentCardStatusLabel(item = {}) {
    return this.$t(this.isPaymentCardOverdue(item)
      ? "ncrm.contracts.home.payments.overdueAmount"
      : "ncrm.contracts.home.payments.nextAmount");
  },
  paymentMethodLabel(item = {}) {
    const method = String(item?.payment_method || "").trim();
    const reference = String(item?.external_reference || "").trim();
    if (method && reference) return `${method} / ${reference}`;
    return method || reference || "-";
  },
  remainingOnboardingCount(item = {}) {
    const fallbackTotal = parseNumeric(item?.onboarding_count, 0);
    return parseNumeric(item?.remaining_onboarding_count, fallbackTotal);
  },
  completedOnboardingCount(item = {}) {
    return parseNumeric(item?.completed_onboarding_count, 0);
  },
  onboardingBadgeLabel(item = {}) {
    const total = parseNumeric(item?.onboarding_count, 0);
    const remaining = this.remainingOnboardingCount(item);
    const completed = this.completedOnboardingCount(item);
    if (total > 0 && completed >= total) return this.$t("ncrm.contracts.home.board.onboardingsDone");
    return this.$t("ncrm.contracts.home.board.onboardingsRemaining", { count: this.formatNumber(remaining) });
  },
  onboardingBadgeColor(item = {}) {
    const total = parseNumeric(item?.onboarding_count, 0);
    return total > 0 && this.completedOnboardingCount(item) >= total ? "success" : "primary";
  },
  boardPaginationSummary() {
    const total = parseNumeric(this.pagination?.total, 0);
    if (!total) return this.$t("ncrm.contracts.home.board.showingCount", { start: "0", end: "0", total: "0" });
    const limit = Math.max(parseNumeric(this.pagination?.limit, 20), 1);
    const currentPage = Math.max(parseNumeric(this.pagination?.page, 1), 1);
    const start = (currentPage - 1) * limit + 1;
    const end = Math.min(total, start + limit - 1);
    return this.$t("ncrm.contracts.home.board.showingCount", {
      start: this.formatNumber(start),
      end: this.formatNumber(end),
      total: this.formatNumber(total),
    });
  },
  contractLabel(item) {
    const contractNumber = String(item?.contract_number || "").trim();
    if (contractNumber) return contractNumber;
    const contractName = String(item?.name || item?.contract_name || "").trim();
    if (contractName) return contractName;
    const contractId = parseNumeric(item?.id, 0);
    return contractId > 0 ? `#${contractId}` : "-";
  },
  displayValue(value) {
    return String(value || "").trim() || "-";
  },
  displayPartyName(companyName, personName) {
    return String(companyName || "").trim() || String(personName || "").trim() || "-";
  },
  restorePreferredView() {
    const preferredView = loadPreferredView({
      pageKey: CONTRACTS_HOME_VIEW_PREFERENCE_KEY,
      items: this.viewItems,
      fallback: this.activeView,
    });
    this.preferredView = preferredView || "board";
    this.activeView = this.preferredView;
  },
  setPreferredView(value) {
    const preferredView = savePreferredView({
      pageKey: CONTRACTS_HOME_VIEW_PREFERENCE_KEY,
      value,
      items: this.viewItems,
    });
    if (!preferredView) return;
    this.preferredView = preferredView;
    this.activeView = preferredView;
  },
  onSearchInput() {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => this.applyFilters(), 380);
  },
  applyFilters() {
    this.pagination.page = 1;
    this.loadCurrentView();
  },
  resolveTableSortState() {
    if (this.activeView !== "table") return null;
    const firstSort = Array.isArray(this.sortBy) && this.sortBy.length ? this.sortBy[0] : null;
    const sortKey = normalizeContractTableSortKey(firstSort?.key);
    const sortOrder = normalizeContractTableSortOrder(firstSort?.order);
    return sortKey && sortOrder ? { key: sortKey, order: sortOrder } : null;
  },
  onSortByChange(value) {
    this.sortBy = Array.isArray(value) ? value : [];
    this.pagination.page = 1;
    this.loadContracts();
  },
  onPageChange(page) {
    const normalized = Number(page || 1);
    this.pagination.page = Number.isFinite(normalized) && normalized > 0 ? Math.trunc(normalized) : 1;
    this.loadCurrentView();
  },
  onItemsPerPageChange(limit) {
    const normalized = Number(limit || 20);
    const nextLimit = Number.isFinite(normalized) && normalized > 0 ? Math.min(Math.trunc(normalized), 200) : 20;
    if (nextLimit === this.pagination.limit) return;
    this.pagination.limit = nextLimit;
    this.pagination.page = 1;
    this.loadCurrentView();
  },
  tableRowProps({ item }) {
    return {
      class: this.hasOverduePayments(item) ? "ncrm-contracts-home__table-row--overdue" : "",
      style: "cursor: pointer",
      ...rowActivationProps(() => this.handleRowClick(null, { item })),
    };
  },
  normalizeRowPayload(payload) {
    if (!payload) return null;
    if (payload.raw) return payload.raw;
    if (payload.item?.raw) return payload.item.raw;
    return payload.item || payload;
  },
  handleRowClick(_, payload) {
    const item = this.normalizeRowPayload(payload);
    const contractId = parseNumeric(item?.id, 0);
    if (contractId) this.openContract(contractId);
  },
  paymentTableRowProps({ item }) {
    return { style: "cursor: pointer", ...rowActivationProps(() => this.handlePaymentRowClick(null, { item })) };
  },
  handlePaymentRowClick(_, payload) {
    const item = this.normalizeRowPayload(payload);
    const contractId = parseNumeric(item?.contract_id, 0);
    if (contractId) this.openContract(contractId);
  },
  onPaymentSubViewChange(value) {
    if (value) this.paymentSubView = value;
    this.pagination.page = 1;
    this.items = [];
    this.loadCurrentView();
  },
  onBoardStatusGroupChange(value) {
    const normalized = normalizeBoardStatusGroup(value);
    if (normalized === this.boardStatusGroup) return;
    this.boardStatusGroup = normalized;
    this.applyFilters();
  },
  loadCurrentView() {
    if (this.activeView === "payments" && this.paymentSubView === "paid") return this.loadPaidPayments();
    return this.loadContracts();
  },
  openContract(contractId) {
    const normalized = parseNumeric(contractId, 0);
    if (!normalized) return;
    this.$router.push({ name: "NcrmContractDetails", params: { contractId: String(normalized) } });
  },
  async loadContracts() {
    this.loading = true;
    try {
      const sortState = this.resolveTableSortState();
      const { data } = await axios.get("/ncrm/contracts", {
        params: buildContractsListRequestParams({
          pagination: this.pagination, filters: this.filters, activeView: this.activeView,
          paymentSubView: this.paymentSubView, boardStatusGroup: this.boardStatusGroup, sortState,
        }),
      });
      this.items = Array.isArray(data?.items) ? data.items : [];
      this.pagination.total = parseNumeric(data?.total, 0);
      this.pagination.pages = parseNumeric(data?.pages, 1);
    } catch (error) {
      console.error("[ncrm-contracts-home] load contracts failed", error);
      this.items = [];
      this.pagination.total = 0;
      this.pagination.pages = 1;
      this.showSnackbar(error?.response?.data?.error || this.$t("ncrm.contracts.messages.loadError"), "error");
    } finally {
      this.loading = false;
    }
  },
  async loadPaidPayments() {
    this.loading = true;
    try {
      const { data } = await axios.get("/ncrm/contracts/payments", {
        params: {
          page: this.pagination.page,
          limit: this.pagination.limit,
          search: this.filters.search.trim() || undefined,
          status: this.filters.status || undefined,
        },
      });
      this.items = Array.isArray(data?.items) ? data.items : [];
      this.pagination.total = parseNumeric(data?.total, 0);
      this.pagination.pages = parseNumeric(data?.pages, 1);
    } catch (error) {
      console.error("[ncrm-contracts-home] load paid payments failed", error);
      this.items = [];
      this.pagination.total = 0;
      this.pagination.pages = 1;
      this.showSnackbar(error?.response?.data?.error || this.$t("ncrm.contracts.messages.loadError"), "error");
    } finally {
      this.loading = false;
    }
  },
};
